import React, { useState, useEffect, useRef } from "react";
import { ABOUT_PAGE } from "../../data/constants";

const timelineData = ABOUT_PAGE.timeline.data;

const TimelineSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);

  const timelineRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  // --- Mouse Drag Handlers ---
  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    startX.current = e.pageX - (timelineRef.current?.offsetLeft || 0);
    scrollLeft.current = timelineRef.current?.scrollLeft || 0;
  };

  const handleMouseLeave = () => {
    isDragging.current = false;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - (timelineRef.current?.offsetLeft || 0);
    const walk = (x - startX.current) * 1.5;
    if (timelineRef.current) {
      timelineRef.current.scrollLeft = scrollLeft.current - walk;
    }
  };

  // --- Touch Handlers ---
  const handleTouchStart = (e: React.TouchEvent) => {
    isDragging.current = true;
    startX.current = e.touches[0].pageX - (timelineRef.current?.offsetLeft || 0);
    scrollLeft.current = timelineRef.current?.scrollLeft || 0;
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    const x = e.touches[0].pageX - (timelineRef.current?.offsetLeft || 0);
    const walk = (x - startX.current) * 1.5;
    if (timelineRef.current) {
      timelineRef.current.scrollLeft = scrollLeft.current - walk;
    }
  };

  // --- Progress Auto Advance ---
  useEffect(() => {
    setProgress(0);
    const totalDuration = 6000;
    const step = 30;
    let elapsed = 0;

    const progressInterval = setInterval(() => {
      if (!paused) {
        elapsed += step;
        setProgress(Math.min((elapsed / totalDuration) * 100, 100));
        if (elapsed >= totalDuration) {
          setActiveIndex((prev) =>
            prev < timelineData.length - 1 ? prev + 1 : 0
          );
          setProgress(0);
          elapsed = 0;
        }
      }
    }, step);

    return () => clearInterval(progressInterval);
  }, [activeIndex, paused]);

  // --- Auto-scroll to active year on small screens ---
  useEffect(() => {
    if (window.innerWidth >= 768) return;
    const scrollToActive = () => {
      const buttons = timelineRef.current?.querySelectorAll("button");
      const activeButton = buttons?.[activeIndex] as HTMLElement;

      if (activeButton && timelineRef.current) {
        const container = timelineRef.current;
        const offsetLeft =
          activeButton.offsetLeft -
          container.offsetWidth / 2 +
          activeButton.offsetWidth / 2;

        container.scrollTo({
          left: offsetLeft,
          behavior: "smooth",
        });
      }
    };

    scrollToActive();
  }, [activeIndex]);

  return (
    <div className="bg-gradient-to-b from-[#001D2B] to-[#207385] text-white py-[64px] md:py-[120px] px-6 md:px-20">
      <div className="flex flex-col md:flex-row justify-between items-start gap-8 md:gap-10">
        {/* Left Title */}
        <div>
          <h2 className="font-utility font-medium text-[30px] md:text-[54px] leading-[120%] tracking-[-0.01em] capitalize text-white">
            {ABOUT_PAGE.timeline.title}
          </h2>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="flex justify-end mb-4 items-start gap-6">
            <div>
              <h3 className="font-utility font-medium text-[30px] md:text-[54px] leading-[120%] tracking-[-0.01em] capitalize text-white">
                {timelineData[activeIndex].year}
              </h3>
              {timelineData[activeIndex].title && (
                <>
                  <h4 className="font-utility text-[18px] md:text-[24px] mt-6 md:mt-8 mb-4 font-medium">
                    {timelineData[activeIndex].title}
                  </h4>
                  <p className="font-inter font-normal h-[80px]  text-[16px] md:text-[20px] leading-[150%] tracking-[-0.005em] text-white/90 max-w-[500px] mt-1 mb-6 md:mb-[80px]">
                    {timelineData[activeIndex].description}
                  </p>
                </>
              )}
            </div>

           
          </div>
        </div>
      </div>

      {/* Timeline Year Slider */}
      <div
        ref={timelineRef}
        className="flex gap-8 overflow-x-auto py-6 px-1 pr-6 select-none cursor-grab w-full max-w-full scrollbar-hide"
        style={{
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        onMouseDown={(e) => { handleMouseDown(e); setPaused(true); }}
        onMouseLeave={() => { handleMouseLeave(); setPaused(false); }}
        onMouseUp={() => { handleMouseUp(); setPaused(false); }}
        onMouseMove={handleMouseMove}
        onTouchStart={(e) => { handleTouchStart(e); setPaused(true); }}
        onTouchEnd={() => { handleTouchEnd(); setPaused(false); }}
        onTouchMove={handleTouchMove}
        onTouchCancel={() => setPaused(false)}
      >
        {timelineData.map((item, index) => (
          <button
            key={index}
            onClick={() => setActiveIndex(index)}
            className={`flex flex-col items-center transition-all duration-400 group ${
              index === activeIndex ? "scale-110" : "opacity-70 hover:opacity-100"
            }`}
            style={{ minWidth: 70 }}
          >
            <span
              className={`text-[12px] md:text-[18px] mt-1 transition-all duration-300 ${
                index === activeIndex ? "text-white font-semibold" : "text-white/60"
              }`}
            >
              {item.year}
            </span>
            <span className="block mt-4 h-[4px] w-[74px] md:w-[100px] bg-white/20 relative overflow-hidden ">
              {index === activeIndex && (
                <span
                  className="absolute left-0 top-0 h-full bg-white transition-all duration-100"
                  style={{ width: `${progress}%` }}
                ></span>
              )}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default TimelineSection;
